import React, { useRef, useState } from "react";
import { X, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

export function CrewLogoField({ logo, onChange }) {
  const { t } = useApp();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/uploads/crew-logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.url); }
    catch (err) { toast.error(err.response?.data?.detail || t("uploadFailed")); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3" data-testid="crew-logo-field">
      <div className="w-16 h-16 border border-foreground bg-background flex items-center justify-center shrink-0 overflow-hidden">
        {logo ? <img src={logo} alt="" className="w-full h-full object-contain" data-testid="crew-logo-preview" /> : <span className="eyebrow !text-[9px] text-muted-foreground">{t("crewLogo")}</span>}
      </div>
      <div className="flex flex-col gap-1.5">
        <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="gg-btn !py-1.5 !px-2.5 text-[10px]" data-testid="crew-logo-upload"><Upload className="w-3.5 h-3.5" />{busy ? t("uploading") : logo ? t("crewLogoReplace") : t("crewLogoUpload")}</button>
        {logo && <button type="button" onClick={() => onChange(null)} className="gg-btn !py-1.5 !px-2.5 text-[10px]" data-testid="crew-logo-remove"><Trash2 className="w-3.5 h-3.5" />{t("crewLogoRemove")}</button>}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="crew-logo-input" />
      </div>
    </div>
  );
}

export default function CrewEditDialog({ crew, onClose, onSaved }) {
  const { t } = useApp();
  const [form, setForm] = useState({ name: crew.name, city: crew.city || "", country: crew.country || "", bio: crew.bio || "", logo: crew.logo || null });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/crews/${crew.crew_id}`, form); toast.success(t("saved")); onSaved(data); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-foreground/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="crew-edit-dialog">
      <div className="bg-background border border-foreground w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-foreground pb-3">
          <div><div className="eyebrow text-primary">{t("organizer")}</div><h2 className="font-display uppercase text-2xl leading-none mt-1">{t("editCrew")}</h2></div>
          <button onClick={onClose} className="gg-icon-btn" data-testid="crew-edit-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <CrewLogoField logo={form.logo} onChange={v => set("logo", v)} />
          <label className="block"><span className="eyebrow !text-[10px] block mb-1">{t("crewName")}</span><input className="gg-input !py-2" value={form.name} onChange={e => set("name", e.target.value)} data-testid="crew-edit-name" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="eyebrow !text-[10px] block mb-1">{t("city")}</span><input className="gg-input !py-2" value={form.city} onChange={e => set("city", e.target.value)} data-testid="crew-edit-city" /></label>
            <label className="block"><span className="eyebrow !text-[10px] block mb-1">{t("country")}</span><input className="gg-input !py-2" value={form.country} onChange={e => set("country", e.target.value)} data-testid="crew-edit-country" /></label>
          </div>
          <label className="block"><span className="eyebrow !text-[10px] block mb-1">{t("crewBio")} <span className="text-muted-foreground normal-case tracking-normal">({form.bio.length}/500)</span></span>
            <textarea className="gg-input !py-2 min-h-[110px]" maxLength={500} value={form.bio} onChange={e => set("bio", e.target.value)} placeholder={t("crewBioPlaceholder")} data-testid="crew-edit-bio" /></label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="gg-btn !py-2 !px-3 text-xs" data-testid="crew-edit-cancel">{t("cancel")}</button>
          <button onClick={save} disabled={saving || form.name.trim().length < 2} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid="crew-edit-save">{t("save")}</button>
        </div>
      </div>
    </div>
  );
}
